import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { ChevronDown } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";

const Accordion = AccordionPrimitive.Root;

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn("border-b", className)}
    {...props}
  />
));
AccordionItem.displayName = "AccordionItem";

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> & {
    showChevronIcon?: boolean;
    chevronElement?: React.ReactNode;
    folderIcons?: {
      closed: React.ElementType;
      open: React.ElementType;
    };
  }
>(
  (
    {
      className,
      children,
      showChevronIcon = true,
      chevronElement,
      folderIcons,
      ...props
    },
    ref,
  ) => {
    const defaultChevron = (
      <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200 group-data-[state=open]/accordion:rotate-180" />
    );

    const content = props.asChild ? (
      children
    ) : (
      <>
        {folderIcons && (
          <>
            <folderIcons.closed className="h-4 w-4 shrink-0 text-muted-foreground group-data-[state=open]/accordion:hidden" />
            <folderIcons.open className="h-4 w-4 shrink-0 text-muted-foreground hidden group-data-[state=open]/accordion:block" />
          </>
        )}
        {children}
        {showChevronIcon && (chevronElement ?? defaultChevron)}
      </>
    );

    return (
      <AccordionPrimitive.Header className="flex">
        <AccordionPrimitive.Trigger
          ref={ref}
          className={cn(
            "group/accordion flex flex-1 items-center justify-between py-4 font-medium transition-all hover:underline",
            className,
          )}
          {...props}
        >
          {content}
        </AccordionPrimitive.Trigger>
      </AccordionPrimitive.Header>
    );
  },
);
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className="overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
    {...props}
  >
    <div className={cn("pb-4 pt-0", className)}>{children}</div>
  </AccordionPrimitive.Content>
));

AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger };
